'use client';

import { useEffect, useState, useTransition } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { BRAND } from '@kit/shared/brand';
import { Alert, AlertDescription, AlertTitle } from '@kit/ui/alert';
import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import { Textarea } from '@kit/ui/textarea';
import { Trans } from '@kit/ui/trans';

import { ContactEmailSchema } from '../_lib/contact-email.schema';
import {
  isContactFormAvailable,
  sendContactEmail,
} from '../_lib/server/server-actions';

export function ContactForm() {
  const [pending, startTransition] = useTransition();
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);

  const [state, setState] = useState({
    success: false,
    error: false,
  });

  // Check if contact form is available on mount
  useEffect(() => {
    isContactFormAvailable().then(setIsAvailable);
  }, []);

  const form = useForm({
    resolver: zodResolver(ContactEmailSchema),
    defaultValues: {
      name: '',
      email: '',
      message: '',
    },
  });

  // Show loading state while checking availability
  if (isAvailable === null) {
    return (
      <div className="text-muted-foreground animate-pulse">
        <Trans i18nKey={'common:loading.default'} defaults="Loading..." />
      </div>
    );
  }

  // Show fallback UI if contact form is not available
  if (isAvailable === false) {
    return <ContactFormUnavailableAlert />;
  }

  if (state.success) {
    return <SuccessAlert />;
  }

  if (state.error) {
    return <ErrorAlert />;
  }

  return (
    <Form {...form}>
      <form
        className={'flex flex-col space-y-4'}
        onSubmit={form.handleSubmit((data) => {
          startTransition(async () => {
            try {
              await sendContactEmail(data);

              setState({ success: true, error: false });
            } catch {
              setState({ error: true, success: false });
            }
          });
        })}
      >
        <FormField
          name={'name'}
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey={'marketing:contactName'} />
                </FormLabel>

                <FormControl>
                  <Input maxLength={200} {...field} />
                </FormControl>

                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          name={'email'}
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey={'marketing:contactEmail'} />
                </FormLabel>

                <FormControl>
                  <Input type={'email'} {...field} />
                </FormControl>

                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          name={'message'}
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey={'marketing:contactMessage'} />
                </FormLabel>

                <FormControl>
                  <Textarea
                    className={'min-h-36'}
                    maxLength={5000}
                    {...field}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            );
          }}
        />

        <Button disabled={pending} type={'submit'}>
          <Trans i18nKey={'marketing:sendMessage'} />
        </Button>
      </form>
    </Form>
  );
}

function SuccessAlert() {
  return (
    <Alert variant={'success'}>
      <AlertTitle>
        <Trans i18nKey={'marketing:contactSuccess'} />
      </AlertTitle>

      <AlertDescription>
        <Trans i18nKey={'marketing:contactSuccessDescription'} />
      </AlertDescription>
    </Alert>
  );
}

function ErrorAlert() {
  return (
    <Alert variant={'destructive'}>
      <AlertTitle>
        <Trans i18nKey={'marketing:contactError'} />
      </AlertTitle>

      <AlertDescription>
        <Trans i18nKey={'marketing:contactErrorDescription'} />
      </AlertDescription>
    </Alert>
  );
}

function ContactFormUnavailableAlert() {
  const supportEmail = BRAND.emails.support;

  return (
    <Alert variant={'default'}>
      <AlertTitle>
        <Trans i18nKey="marketing:contactUnavailable.title" />
      </AlertTitle>

      <AlertDescription>
        <p className="mb-3">
          <Trans i18nKey="marketing:contactUnavailable.description" />
        </p>
        <p className="mb-2 font-medium">
          <Trans i18nKey="marketing:contactUnavailable.reachUs" />
        </p>
        <ul className="ml-2 space-y-2">
          <li className="flex items-center gap-2">
            <span>
              <Trans i18nKey="marketing:contactUnavailable.emailLabel" />{' '}
              <a
                href={`mailto:${supportEmail}`}
                className="text-primary underline hover:no-underline"
              >
                {supportEmail}
              </a>
            </span>
          </li>
        </ul>
      </AlertDescription>
    </Alert>
  );
}
